import { useState } from "react";
import { Surface, SurfaceBackground } from "azure-devops-ui/Surface";
import { Page } from "azure-devops-ui/Page";
import { Card } from "azure-devops-ui/Card";
import MiniAppCommonCheck from "../Modules/MiniApps/MiniAppCommonCheck/MiniAppCommonCheck";
import MiniAppReportDialog from "../Modules/MiniApps/MiniAppReportDialog/MiniAppReportDialog";
import PoliCheckResultTable from "./PolicheckResultTable/PolicheckResultTable";
import { formatDateToUTCStringDate } from "../Utilities/CommonFunction";
import { IPoliCheckCheckItem, ICurrentRow } from "./PoliCheckInterfaces";
import "./PoliCheck.css";
import CustomMessageAlert from "../Modules/CustomMessageAlert/CustomMessageAlert";
import { getAPIService } from "../../redux/Actions/PowerDataQuery";
import { useMsal, useAccount } from "@azure/msal-react";
import CustomConfirmDialog from "../Modules/CustomConfirmDialog/CustomConfirmDialog"
import CommonLoading from "../Modules/CommonLoading/CommonLoading";
const apiService = getAPIService().taskService;

interface IScanResultState {
    allCount: number,
    scannedCount: number,
    errorCount: number,
    warningCount: number,
    successCount: number,
    reportedCount: number,
    scanStatus: string
}

export default function PoliCheck() {
    const [PoliCheckList, setPoliCheckList] = useState<IPoliCheckCheckItem[]>([]);

    const [scanResultState, setScanResultState] = useState<IScanResultState>({
        allCount: 0,
        scannedCount: 0,
        errorCount: 0,
        warningCount: 0,
        successCount: 0,
        reportedCount: 0,
        scanStatus: ''
    })
    const [isReportDialogHidden, setIsReportDialogHidden] = useState<boolean>(true);
    const [isDialogLoading, setIsDialogLoading] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

    const [currentExecuteRow, setCurrentExecuteRow] = useState<ICurrentRow>();

    const [isScanning, setIsScanning] = useState<boolean>(false)

    // error alert message
    const [showCalloutError, setShowCalloutError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')

    const [inputScanUrl, setInputScanUrl] = useState<string>('');
    const [scanCacheData, setScanCacheData] = useState<[]>([])
    const { accounts } = useMsal();
    const userAccount = useAccount(accounts[0]);

    const handleScanData = (scanData) => {
        return scanData.map(item => {
            return {
                content: item.policheckText,
                context: item.context,
                category: item.category
            }
        })
    }

    const sortCheckListByDefault = (poliCheckIssues) => {
        poliCheckIssues.forEach(item => {
            if(item.status === 'Passed') {
                item.priority = 1
            }else if(item.status === 'Waiting') {
                item.priority = 2
            }else {
                item.priority = 3
            }
        })
        poliCheckIssues.sort((a,b)=> {
            // sort by status
            if(a.priority === b.priority) {
                // a.status === b.status, sort by category
                if(a.category === b.category) {
                    // a.category === b.category, sort by entity name
                    return a.content.localeCompare(b.content)
                }
                return a.category.localeCompare(b.category)
            }
            return a.priority - b.priority
        })
        return poliCheckIssues
    }

    async function scanLink(scanUrl?: string) {
        setPoliCheckList([])
        setIsScanning(true);
        setScanResultState({
            allCount: 0,
            scannedCount: 0,
            errorCount: 0,
            warningCount: 0,
            successCount: 0,
            reportedCount: 0,
            scanStatus: 'Running'
        })
        try {
            let poliCheckScanData;
            if(scanUrl) {
                poliCheckScanData = await apiService.getPoliCheckScanData(scanUrl)
                setScanCacheData(poliCheckScanData)
                setInputScanUrl(scanUrl);
            }else {
                poliCheckScanData = scanCacheData
            }
            if(poliCheckScanData.length > 0) {
                const data = await apiService.getIssueList(scanUrl? scanUrl: inputScanUrl, userAccount.username, handleScanData(poliCheckScanData), 'PoliCheck')
                const poliCheckIssues = data.issueData
                let reportedIssues = 0
                //TODO status need get by backend
                poliCheckIssues.forEach(item => {
                    if(item.issueId) {
                        item.status = 'Passed'
                        reportedIssues++
                    }
                })
                setPoliCheckList(sortCheckListByDefault(poliCheckIssues))
                setScanResultState({
                    ...scanResultState,
                    allCount: poliCheckIssues?.length,
                    reportedCount: reportedIssues,
                    scanStatus: 'Existing'
                })
            }else {
                setScanResultState({
                    ...scanResultState,
                    scanStatus: 'Existing'
                })
            }
        }catch(error) {
            setScanResultState({
                ...scanResultState,
                scanStatus: 'Failed'
            })
        }finally {
            setIsScanning(false);
        }
    }

    function openDialog(buttonType: string, currentRowData: ICurrentRow) {
        setCurrentExecuteRow(currentRowData);
        if (buttonType === "Report as false positive") {
            setIsReportDialogHidden(false);
        }
    }

    const closeReport = (): void => {
        setIsReportDialogHidden(true);
    };

    const getExpireTime = (): Date => {
        const time = new Date();
        time.setTime(time.getTime() + 24*60*60*1000*180);
        return time;
    }

    const confirmReport = async(description: string) => {
        setIsDialogLoading(true)
        const today = formatDateToUTCStringDate(new Date())
        const createParam = {
            issueData:[{
                articleUrl: inputScanUrl,
                content: currentExecuteRow.tableItem.content,
                context: currentExecuteRow.tableItem.context,
                category: currentExecuteRow.tableItem.category,
                status: currentExecuteRow.tableItem.status,
                exceptionType: 'False Positive',
                sourceApp: 'SONIC',
                reporter: userAccount.username,
                reportTime: today,
                riskArea: 'PoliCheck',
                businessJustification: description,
                startTime: today,
                expireTime: formatDateToUTCStringDate(getExpireTime()),
            }]
        }
        try {
            const data = await apiService.createIssueReport(createParam);
            if(data.success) {
                setShowSuccessMessage(true)
                setIsReportDialogHidden(true)
                setTimeout(() => {
                    setShowSuccessMessage(false)
                }, 3000);
                await scanLink();
            }
        }catch(error){
            setErrorMessage('Sorry, some errors occurred, please try again later')
            setShowCalloutError(true)
        }finally {
            setIsDialogLoading(false);
        }
    };

    return (
        <div style={{ width: "100%" }}>
            <Surface background={SurfaceBackground.neutral}>
                <Page className="pipelines-page flex-grow">
                    <div className="page-content page-content-top">
                        <CustomMessageAlert
                            showAlert={showSuccessMessage}
                            message="Report false positive successfully!"
                            type="success"
                        />
                        <div className="ms-Grid">
                            <MiniAppCommonCheck
                                showResultStateBar={false}
                                scanResultState={scanResultState}
                                startCheck={scanLink}
                                featureName="PoliCheck"
                                statusLabel="Check status"
                                resultLabel="Scanned issues"
                            />
                            <div className="ms-Grid-row">
                                <Card>
                                    <PoliCheckResultTable
                                        tableData={PoliCheckList}
                                        openDialog={openDialog}
                                    />
                                </Card>
                            </div>
                        </div>
                    </div>
                    <CommonLoading
                        label="Loading..."
                        isLoading={isScanning}
                     />
                </Page>
            </Surface>
            <CustomConfirmDialog
                showDialog={showCalloutError}
                title="Action Error"
                dialogType="error"
                onConfirmDismiss={()=>{ setShowCalloutError(false) }}
                content={errorMessage}
                confirmButtonLabel="Dismiss"
            />
            <MiniAppReportDialog
                hideReportDialog={isReportDialogHidden}
                close={closeReport}
                confirm={confirmReport}
                isLoading={isDialogLoading}
            />
        </div>
    );
}
